import api from "./index";
import generateHeaders from "../utils/generateHeaders";

/**
 * Service para operações relacionadas ao Upload Flow
 * Mantém exatamente a mesma estrutura das chamadas originais para preservar funcionalidade
 */
class UploadFlowService {
  /**
   * Busca os perfis disponíveis
   */
  async getProfiles(token) {
    const headers = generateHeaders(token);
    return await api.get("/upload-flow/profiles", { headers: headers.headers });
  }

  /**
   * Busca um flow específico por ID
   * @param {string} id - ID do flow
   */
  async getFlow(id, token) {
    const headers = generateHeaders(token);
    return await api.get(`/upload-flow/${id}`, { headers: headers.headers });
  }

  /**
   * Cria um novo flow
   * @param {Object} uploadFlowRequest - Dados do flow
   */
  async createFlow(uploadFlowRequest, token) {
    const headers = generateHeaders(token);
    return await api.post("/upload-flow", uploadFlowRequest, headers);
  }

  /**
   * Atualiza um flow existente
   * @param {string} id - ID do flow
   * @param {Object} uploadFlowRequest - Dados do flow
   */
  async updateFlow(id, uploadFlowRequest, token) {
    const headers = generateHeaders(token);
    return await api.put(`/upload-flow/${id}`, uploadFlowRequest, headers);
  }

  /**
   * Remove um documento do flow
   * @param {string} documentConfigId - ID do documento
   */
  removeDocumentFromFlow(documentConfigId, token) {
    const headers = generateHeaders(token);
    return api.delete(`/upload-flow/documentConfig/${documentConfigId}`, {
      headers: headers.headers,
    });
  }

  /**
   * Deleta um flow
   * @param {string} flowId - ID do flow
   */
  async deleteFlow(flowId, token) {
    const headers = generateHeaders(token);
    return await api.delete(`/upload-flow/${flowId}`, {
      headers: headers.headers,
    });
  }
}

export default new UploadFlowService();

// 🧩 Função utilitária para montar parâmetros padrão
const buildParams = ({
  page = 0,
  size = 10,
  search = "",
  document,
  status,
  uploadOrigin,
  sortBy,
  sortDirection,
  extra = {},
}) => ({
  page,
  size,
  ...(search?.trim() && { search: search.trim() }),
  ...(document && document !== "todos" && { document }),
  ...(status && status !== "todos" && { status }),
  ...(uploadOrigin && uploadOrigin !== "todos" && { uploadOrigin }),
  ...(sortBy && { sortBy, sortDirection }),
  ...extra, // permite incluir params adicionais como "dispense"
});

// 🟢 POST - Marcar/desmarcar documentos como dispensados
export const postDispenseStatusService = async (
  body,
  dispense,
  headers = {}
) => {
  return await api.post("/upload-flow/documentconfig/dispense-status", body, {
    params: { dispense },
    headers,
  });
};

// 🔵 GET - Documentos NÃO dispensados
export const getNotDispensedDocumentsService = async ({
  eventId,
  page,
  size,
  search,
  document,
  status,
  sortBy,
  sortDirection,
  headers = {},
}) => {
  const params = buildParams({
    page,
    size,
    search,
    document,
    status,
    sortBy,
    sortDirection,
  });

  return await api.get(`/upload-flow/notdispensed-documents/${eventId}`, {
    params,
    headers,
  });
};

// 🟣 GET - Documentos dispensados
export const getDispensedDocumentsService = async ({
  eventId,
  page,
  size,
  search,
  document,
  status,
  sortBy,
  sortDirection,
  headers = {},
}) => {
  const params = buildParams({
    page,
    size,
    search,
    document,
    status,
    sortBy,
    sortDirection,
  });

  return await api.get(`/upload-flow/dispensed-documents/${eventId}`, {
    params,
    headers,
  });
};

// 🟠 GET - Documentos gerais do evento
export const getEventDocumentsService = async ({
  eventId,
  page,
  size,
  search,
  document,
  status,
  uploadOrigin,
  sortBy,
  sortDirection,
  headers = {},
}) => {
  const rawParams = {
    page,
    size,
    search,
    document,
    status,
    uploadOrigin,
    sortBy,
    sortDirection,
  };

  const params = buildParams(rawParams);

  return await api.get(`/upload-flow/event/${eventId}/documents`, {
    params,
    headers,
  });
};

/**
 * Busca APENAS os contadores de documentos de um evento (endpoint otimizado)
 *
 * Este endpoint é muito mais rápido que buscar documentos completos,
 * pois retorna apenas agregações numéricas sem carregar objetos.
 *
 * Use quando você só precisa exibir badges, cards de resumo ou indicadores.
 *
 * @param {Object} params - Parâmetros da busca
 * @param {number} params.eventId - ID do evento (obrigatório)
 * @param {number} [params.companyId] - Filtrar por empresa específica
 * @param {string} [params.uploadOrigin] - Filtrar por origem (ECOMMERCE, PARTNER, CLIENT, CONTRACT)
 * @param {number} [params.approvalInstanceId] - Filtrar por instância de aprovação
 * @param {number} [params.documentChecklistId] - Filtrar por checklist
 * @param {string} [params.documentType] - Filtrar por tipo de documento
 * @param {boolean} [params.dispensed] - Filtrar documentos dispensados
 * @param {string} [params.documentStatus] - Filtrar por status
 * @param {Object} [params.headers={}] - Headers customizados (ex: Authorization)
 *
 * @returns {Promise} Response com:
 *   - pendingCount: number (documentos com pelo menos 1 aprovação pendente)
 *   - approvedCount: number (documentos com TODAS aprovações aprovadas)
 *   - rejectedCount: number (documentos com pelo menos 1 aprovação rejeitada)
 *   - waitingSendCount: number (documentos com status WAITING_SEND)
 *   - dispensedCount: number (documentos dispensados)
 *
 * @example
 * // Buscar contadores de um evento
 * const response = await getDocumentCountersService({
 *   eventId: 12345,
 *   headers: { Authorization: `Bearer ${token}` }
 * });
 * console.log(response.data.pendingCount); // 5
 * console.log(response.data.approvedCount); // 10
 *
 * @example
 * // Com filtros
 * const response = await getDocumentCountersService({
 *   eventId: 12345,
 *   uploadOrigin: 'CLIENT',
 *   companyId: 987,
 *   headers: authHeaders
 * });
 */
export const getDocumentCountersService = async ({
  eventId,
  companyId,
  uploadOrigin,
  approvalInstanceId,
  documentChecklistId,
  documentType,
  dispensed,
  documentStatus,
  headers = {},
}) => {
  // Monta apenas os query params que foram fornecidos
  const params = {};

  if (companyId !== undefined && companyId !== null) {
    params.companyId = companyId;
  }
  if (uploadOrigin) {
    params.uploadOrigin = uploadOrigin;
  }
  if (approvalInstanceId !== undefined && approvalInstanceId !== null) {
    params.approvalInstanceId = approvalInstanceId;
  }
  if (documentChecklistId !== undefined && documentChecklistId !== null) {
    params.documentChecklistId = documentChecklistId;
  }
  if (documentType) {
    params.documentType = documentType;
  }
  if (dispensed !== undefined && dispensed !== null) {
    params.dispensed = dispensed;
  }
  if (documentStatus) {
    params.documentStatus = documentStatus;
  }

  return await api.get(`/upload-flow/event/${eventId}/documents/counters`, {
    params,
    headers,
  });
};

export const getDocumentConfigsService = async (eventId) => {
  return await api.get("/upload-flow/document-configs", {
    params: { eventId },
  });
};
export const getUploadFlowEventsService = async (
  flowId,
  eventEditionCode,
  headers
) => {
  return await api.get(`/upload-flow/${flowId}/events/${eventEditionCode}`, {
    headers,
  });
};

export const getDocumentResendHistory = async (id, headers) => {
  return await api.get(`/upload-flow/documents/${id}/resend-history`, {
    headers,
  });
};

/**
 * Busca todos os produtos do Salesforce disponíveis para seleção
 * @param {string} token - Token de autenticação
 * @returns {Promise} Lista de produtos do Salesforce
 */
export const getSalesforceProductsService = async (token) => {
  const headers = generateHeaders(token);
  return await api.get("/product/get-all-salesforce-contract-products", {
    headers: headers.headers,
  });
};

const REJECTION_REASONS_ENDPOINT = "/rejection-reasons";

export const DEFAULT_REJECTION_REASONS_PAGINATION = Object.freeze({
  page: 0,
  size: 10,
  sort: "createdAt",
  direction: "DESC",
});

const buildRejectionReasonsParams = ({
  page = DEFAULT_REJECTION_REASONS_PAGINATION.page,
  size = DEFAULT_REJECTION_REASONS_PAGINATION.size,
  sort = DEFAULT_REJECTION_REASONS_PAGINATION.sort,
  direction = DEFAULT_REJECTION_REASONS_PAGINATION.direction,
  search = "",
} = {}) => ({
  page,
  size,
  sort,
  direction,
  search,
});

export const getRejectionReasonsService = async (
  {
    page,
    size,
    sort,
    direction,
    search,
  } = DEFAULT_REJECTION_REASONS_PAGINATION,
  token
) => {
  const { headers } = generateHeaders(token);

  const params = buildRejectionReasonsParams({
    page,
    size,
    sort,
    direction,
    search,
  });

  return await api.get(REJECTION_REASONS_ENDPOINT, {
    params,
    headers,
  });
};

export const getRejectionReasonByIdService = async (id, token) => {
  const { headers } = generateHeaders(token);
  return await api.get(`${REJECTION_REASONS_ENDPOINT}/${id}`, { headers });
};

export const postRejectionReasonService = async ({ description }, token) => {
  const config = generateHeaders(token);
  return await api.post(REJECTION_REASONS_ENDPOINT, { description }, config);
};

export const putRejectionReasonService = async (id, { description }, token) => {
  const config = generateHeaders(token);
  return await api.put(
    `${REJECTION_REASONS_ENDPOINT}/${id}`,
    { description },
    config
  );
};

export const deleteRejectionReasonService = async (
  id,
  token,
  { confirm } = {}
) => {
  const { headers } = generateHeaders(token);
  return await api.delete(`${REJECTION_REASONS_ENDPOINT}/${id}`, {
    headers,
    ...(confirm && { params: { confirm: true } }),
  });
};

const PACKAGE_DOCUMENTS_ENDPOINT = (eventId, uploadFlowId) =>
  `/upload-flow/event/${eventId}/packages/${uploadFlowId}/documents`;

export const fetchPackageInternalDocuments = async ({
  eventId,
  uploadFlowId,
  token,
  params = {},
}) => {
  const response = await api.get(
    PACKAGE_DOCUMENTS_ENDPOINT(eventId, uploadFlowId),
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        Authorization: `Bearer ${token}`,
      },
      params,
    }
  );

  return response?.data ?? [];
};
